import { useCallback } from 'react'
import { Frequency, RRule } from '@guestgoodie/helpers'
import { Trans, useLingui } from '@lingui/react/macro'

import Field from '@/components/ui/Field'
import Dropdown from '@/components/ui/Dropdown'
import { useFrequencyOptions } from '../utils/helpers'
import { IntervalInput } from './IntervalInput'
import { EndSelect } from './EndSelect'
import { WeekdaySelect } from './WeekdaySelect'
import { MonthlyOptions } from './MonthlyOptions'
import { YearlyOptions } from './YearlyOptions'

interface RRuleInputProps {
  rrule: RRule
  onChange: (rrule: RRule) => void
}

export function RRuleInput(props: RRuleInputProps) {
  const { rrule, onChange } = props

  const { t } = useLingui()
  const frequencyOptions = useFrequencyOptions()

  const onEnabledChanged = useCallback(
    (isEnabled: boolean) => {
      const newRRule = new RRule({
        dtstart: null,
        byminute: rrule.origOptions.byminute,
        byhour: rrule.origOptions.byhour,
        bysecond: 0,
        freq: Frequency.DAILY,
        // If the user doesn't want to have a recurrence we set it to count 1
        count: isEnabled ? null : 1
      })

      onChange(newRRule)
    },
    [rrule.origOptions, onChange]
  )

  const onChangeOption = useCallback(
    (options: Partial<RRule['origOptions']>) => {
      const newRRule = new RRule({
        ...rrule.origOptions,
        ...options,
        dtstart: null,
        bysecond: 0
      })

      onChange(newRRule)
    },
    [rrule.origOptions, onChange]
  )

  const onChangeFrequency = useCallback(
    (freq: Frequency) => {
      const newRRule = new RRule({
        ...rrule.origOptions,
        freq,
        dtstart: null,
        bysecond: 0,
        byweekday: undefined,
        bymonthday: undefined,
        bymonth: undefined
      })

      onChange(newRRule)
    },
    [rrule.origOptions, onChange]
  )

  return (
    <Field
      label={t({
        comment: 'Asks the user if he wants to repeate a recurrence',
        message: 'Repeating?'
      })}
      canBeDisabled
      onEnabledChange={onEnabledChanged}
      size="small"
    >
      <div className="flex flex-col gap-2">
        <label className="flex flex-row items-center gap-2">
          <span>
            <Trans>Frequency</Trans>:
          </span>
          <Dropdown<Frequency>
            options={frequencyOptions}
            onChange={onChangeFrequency}
            value={rrule.options.freq}
          />
        </label>
        <IntervalInput
          freq={rrule.options.freq}
          value={rrule.options.interval}
          onChange={interval => onChangeOption({ interval })}
        />
        {rrule.options.freq === Frequency.WEEKLY && (
          <WeekdaySelect
            value={rrule.origOptions.byweekday}
            onChange={byweekday => onChangeOption({ byweekday })}
          />
        )}
        {rrule.options.freq === Frequency.MONTHLY && (
          <MonthlyOptions value={rrule} onChangeOption={onChangeOption} />
        )}
        {rrule.options.freq === Frequency.YEARLY && (
          <YearlyOptions value={rrule} onChangeOption={onChangeOption} />
        )}
        <EndSelect
          value={rrule.options.until}
          onChange={until => onChangeOption({ until })}
        />
      </div>
    </Field>
  )
}
